import { motion } from 'framer-motion'

interface OrbitRingProps {
  size?: number
  progress: number
  isProcessing: boolean
  isCompleted: boolean
  score?: number | null
  processingColor?: string
}

function getScoreColor(score: number): string {
  if (score < 40) return '#ef4444'
  if (score < 60) return '#f59e0b'
  return '#10b981'
}

export default function OrbitRing({
  size = 32,
  progress,
  isProcessing,
  isCompleted,
  score,
  processingColor,
}: OrbitRingProps) {
  const sw = size <= 28 ? 2.5 : 3
  const c = size / 2
  const r = (size - sw * 2) / 2
  const circ = 2 * Math.PI * r
  const fill = isCompleted ? 100 : progress
  const offset = circ - (fill / 100) * circ

  const purple = processingColor ?? '#a855f7'
  const completedColor = score != null ? getScoreColor(score) : '#10b981'
  const color = isCompleted ? completedColor : purple

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full h-full"
        style={{ transform: 'rotate(-90deg)' }}
      >
        {/* Background ring */}
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={isCompleted ? `${completedColor}20` : 'rgba(168,85,247,0.12)'}
          strokeWidth={sw}
        />
        {/* Progress arc — fills gradually */}
        <motion.circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: offset }}
          transition={{
            duration: isCompleted ? 0.6 : 2,
            ease: isCompleted ? [0.34, 1.56, 0.64, 1] : 'easeOut',
          }}
          style={{ filter: `drop-shadow(0 0 3px ${color}40)` }}
        />
      </svg>

      {/* Rotating tracer arc — processing only */}
      {isProcessing && (
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'linear' }}
        >
          <svg
            viewBox={`0 0 ${size} ${size}`}
            className="w-full h-full"
            style={{ transform: 'rotate(-90deg)' }}
          >
            <circle
              cx={c}
              cy={c}
              r={r}
              fill="none"
              stroke={purple}
              strokeWidth={sw - 0.5}
              strokeLinecap="round"
              strokeDasharray={`${circ * 0.2} ${circ * 0.8}`}
              opacity={0.4}
              style={{ filter: 'drop-shadow(0 0 2px rgba(168,85,247,0.3))' }}
            />
          </svg>
        </motion.div>
      )}

      {/* Score number — completion only */}
      {isCompleted && score != null && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25, delay: 0.3 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            className="font-bold tabular-nums leading-none"
            style={{
              fontSize: size <= 28 ? '8px' : size <= 32 ? '9px' : '11px',
              color: completedColor,
            }}
          >
            {score}
          </span>
        </motion.div>
      )}

      {/* Pulse glow — completion */}
      {isCompleted && (
        <motion.div
          className="absolute -inset-0.5 rounded-full pointer-events-none"
          animate={{
            boxShadow: [
              `0 0 0 0px ${completedColor}00`,
              `0 0 0 3px ${completedColor}15`,
              `0 0 0 0px ${completedColor}00`,
            ],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
    </div>
  )
}
